<template>
  <div class="edit-workflow-drawer flex flex-col h-full overflow-y-auto">
    <!-- Header -->
    <div class="flex items-center gap-3 py-4 border-b border-surface-200">
      <div class="icon-box" :style="iconStyle">
        <i :class="['pi', headerIcon]"></i>
      </div>
      <div class="flex-1 min-w-0">
        <div class="font-medium text-[15px]">{{ isBuiltIn ? localWorkflow.name : (localWorkflow.name || 'משימה חדשה') }}</div>
        <div class="text-xs text-surface-500 mt-0.5">
          {{ isBuiltIn ? 'משימה מובנית' : 'משימה מותאמת אישית' }}
        </div>
      </div>
      <InputSwitch v-model="localWorkflow.enabled" />
    </div>

    <!-- Body -->
    <div class="flex-1 overflow-y-auto py-4">
      <!-- Custom-only: name + description -->
      <template v-if="!isBuiltIn">
        <div class="section-label">שם המשימה</div>
        <InputText
          v-model="localWorkflow.name"
          placeholder="לדוגמה: הצעת מחיר להסרת שיער בלייזר"
          class="w-full mb-4"
          dir="rtl"
        />
      </template>

      <!-- When to trigger -->
      <div class="section-label flex items-center gap-1">
        מתי הבוט יפעיל את המשימה
        <i v-tooltip.top="'תיאור בשפה חופשית של ההודעה/בקשה שתפעיל את המשימה.'" class="pi pi-info-circle text-[12px] text-surface-400"></i>
      </div>
      <Textarea
        v-model="localWorkflow.whenToTrigger"
        rows="3"
        class="w-full text-[13px] mb-4"
        dir="rtl"
        placeholder="לדוגמה: כשהלקוח שואל כמה עולה טיפול שלא במחירון."
      />

      <!-- Instructions -->
      <div class="section-label flex items-center gap-1">
        איך הבוט יבצע את המשימה
        <i v-tooltip.top="'הוראות שלב-שלב לבוט. תיאור מה הוא צריך לעשות, אילו שאלות לשאול, ובאיזה כלים להשתמש.'" class="pi pi-info-circle text-[12px] text-surface-400"></i>
      </div>
      <Textarea
        v-model="localWorkflow.instructions"
        rows="4"
        class="w-full text-[13px] mb-4"
        dir="rtl"
        placeholder="לדוגמה: שאל את הלקוח לאיזה אזור גוף מתייחס הטיפול, בקש תמונה של האזור, וחפש את השירות הקרוב ביותר במחירון לחישוב הערכת מחיר."
      />

      <!-- Definition of done -->
      <div class="section-label flex items-center gap-1">
        מתי המשימה הושלמה
        <i v-tooltip.top="'התנאי שבו הבוט יסיים את המשימה ויעצור.'" class="pi pi-info-circle text-[12px] text-surface-400"></i>
      </div>
      <Textarea
        v-model="localWorkflow.definitionOfDone"
        rows="2"
        class="w-full text-[13px] mb-4"
        dir="rtl"
        placeholder="לדוגמה: הלקוח קיבל הערכת מחיר וקיבל הודעה שבעל העסק יחזור אליו."
      />

      <!-- Built-in: tools list (read-only) -->
      <template v-if="isBuiltIn && builtInMeta">
        <div class="section-label">כלים שהמשימה משתמשת בהם</div>
        <div class="info-box mb-4">
          <i class="pi pi-info-circle text-surface-500 text-sm"></i>
          <span class="text-xs text-surface-600 leading-relaxed">
            משימה זו משתמשת ב-{{ builtInMeta.toolsUsed.length }} כלים מובנים. את היכולות לא ניתן לערוך,
            אבל אפשר לשנות את ההנחיות שיובילו את הבוט.
          </span>
        </div>

        <!-- Cancel-policy link for reschedule/cancel -->
        <div v-if="needsPolicyNote" class="policy-note mb-4">
          <i class="pi pi-shield text-surface-600 text-sm"></i>
          <div class="flex-1">
            <div class="text-sm font-medium mb-1">מדיניות ביטולים נקבעת בהגדרות</div>
            <div class="text-xs text-surface-600 leading-relaxed mb-2">
              הבוט יבדוק את ההגדרה <strong>"ביטול תורים עד X שעות לפני"</strong> שמוגדרת בעמוד ההגדרות וייכבד אותה אוטומטית.
            </div>
            <Button
              label="לעריכת מדיניות הביטולים"
              icon="pi pi-arrow-left"
              iconPos="right"
              size="small"
              text
              class="!p-0"
              @click="goToCancelPolicy"
            />
          </div>
        </div>
      </template>

      <!-- Custom: tool picker -->
      <template v-else>
        <div class="section-label">יכולות שהבוט יכול להשתמש בהן</div>
        <div class="flex flex-col gap-2 mb-4">
          <label
            v-for="tool in toolMetas"
            :key="tool.tool"
            class="tool-row"
            :class="{ selected: hasTool(tool.tool) }"
          >
            <Checkbox
              :modelValue="hasTool(tool.tool)"
              :binary="true"
              @update:modelValue="toggleTool(tool.tool, $event)"
            />
            <div class="tool-icon">
              <i :class="['pi', tool.icon]"></i>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium">{{ tool.name }}</div>
              <div class="text-xs text-surface-500 leading-snug">{{ tool.description }}</div>
            </div>
          </label>
        </div>

        <!-- Owner notification flag -->
        <div class="flex items-center justify-between gap-3 p-3 bg-surface-50 border border-surface-200 rounded-[10px]">
          <div class="flex flex-col gap-0.5 flex-1">
            <span class="text-sm font-medium">התראה אוטומטית בסיום</span>
            <span class="text-xs text-surface-500">כאשר המשימה מסתיימת — שלח התראה לבעל העסק עם סיכום השיחה.</span>
          </div>
          <InputSwitch v-model="localWorkflow.notifyOwnerOnDone" />
        </div>
      </template>
    </div>

    <drawer-footer
      :approveLoading="saving"
      :approveDisable="!canSave"
      @approve-click="save"
      @cancel-click="cancel"
    />
  </div>
</template>

<script lang="ts" setup>
import type { IAiWorkflow } from '@torim-app/types';
import { CustomWorkflowToolEnum, WorkflowTypeEnum } from '@torim-app/types';
import { computed, reactive, ref } from 'vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import { useDrawer } from '@torim-app/shared-components';
import { useRouter } from 'vue-router';
import { AdminRouteNames } from '@app/router/route-names';
import { builtInMetaByType, customWorkflowToolMetas } from './workflow-types-config';

const props = defineProps<{
  workflow: IAiWorkflow;
  onSave: (workflow: IAiWorkflow) => Promise<void>;
}>();

const { closeDrawer } = useDrawer();
const router = useRouter();
const saving = ref(false);

const localWorkflow = reactive<IAiWorkflow>({
  ...props.workflow,
  enabledTools: props.workflow.enabledTools ? [...props.workflow.enabledTools] : [],
});

const isBuiltIn = computed(() => localWorkflow.type !== WorkflowTypeEnum.Custom);
const builtInMeta = computed(() => builtInMetaByType[localWorkflow.type]);
const needsPolicyNote = computed(() =>
  localWorkflow.type === WorkflowTypeEnum.Cancel ||
  localWorkflow.type === WorkflowTypeEnum.Reschedule,
);

const headerIcon = computed(() => builtInMeta.value?.icon || 'pi-sparkles');
const iconStyle = computed(() => {
  const color = builtInMeta.value?.color || '#6831d0';
  return { backgroundColor: color + '18', color };
});

const toolMetas = customWorkflowToolMetas;

const hasTool = (tool: CustomWorkflowToolEnum) =>
  localWorkflow.enabledTools?.includes(tool) ?? false;

const toggleTool = (tool: CustomWorkflowToolEnum, on: boolean) => {
  const current = localWorkflow.enabledTools ?? [];
  if (on) {
    if (!current.includes(tool)) localWorkflow.enabledTools = [...current, tool];
  } else {
    localWorkflow.enabledTools = current.filter(t => t !== tool);
  }
};

const canSave = computed(() => {
  if (isBuiltIn.value) {
    return !!localWorkflow.whenToTrigger?.trim()
      && !!localWorkflow.instructions?.trim()
      && !!localWorkflow.definitionOfDone?.trim();
  }
  return !!localWorkflow.name?.trim()
    && !!localWorkflow.whenToTrigger?.trim()
    && !!localWorkflow.instructions?.trim()
    && !!localWorkflow.definitionOfDone?.trim();
});

const save = async () => {
  saving.value = true;
  try {
    await props.onSave({ ...localWorkflow, enabledTools: [...(localWorkflow.enabledTools ?? [])] });
    closeDrawer();
  } finally {
    saving.value = false;
  }
};

const cancel = () => closeDrawer();

const goToCancelPolicy = () => {
  closeDrawer();
  router.push({ name: AdminRouteNames.Configurations, query: { tab: '1' } });
};
</script>

<style scoped lang="scss">
.edit-workflow-drawer {
  .icon-box {
    width: 2.625rem;
    height: 2.625rem;
    border-radius: 0.625rem;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    i {
      font-size: 1.25rem;
    }
  }

  .section-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--p-surface-700);
    margin-bottom: 8px;
    letter-spacing: 0.3px;
  }

  .info-box {
    background: var(--p-surface-50);
    border: 1px solid var(--p-surface-200);
    border-radius: 10px;
    padding: 12px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .policy-note {
    background: #fff7ed;
    border: 1px solid #fed7aa;
    border-radius: 10px;
    padding: 14px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
  }

  .tool-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid var(--p-surface-200);
    background: var(--p-surface-0);
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;

    &:hover {
      background: var(--p-surface-50);
    }

    &.selected {
      border-color: rgb(var(--primary));
      background: linear-gradient(135deg, #f5f4fe 0%, #f0eefa 100%);
    }
  }

  .tool-icon {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--p-primary-50);
    color: var(--p-primary-700);
    flex-shrink: 0;

    i {
      font-size: 14px;
    }
  }
}
</style>
